import { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, LinearProgress, TextField, Typography } from '@mui/material';
import apiRequest from '../../api/request';

const POLL_INTERVAL_MS = 2000;

// Tech-team-only: this triggers real LeadSquare API calls and writes
// directly to `leads`, not something Finance/other viewers of this screen
// (e.g. the Finance Manager who reviewed the rest of this page) should be
// poking at. Same department-name check src/routing/route/myworkspace/
// HrRoute.jsx already uses for its own gate - the closest existing
// precedent in this codebase for "show this only to department X", not a
// new pattern. Backend enforces the same restriction on the trigger
// endpoint itself (lcr_config.controller.js) - this hiding is a UX
// convenience, not the actual security boundary.
const isTechTeam = () => {
  try {
    return JSON.parse(localStorage.getItem('cred'))?.profile?.departmentName === 'Technical Department';
  } catch {
    return false;
  }
};

// Triggers the historical lead_ls_source/lead_ls_type_2 backfill
// (src/warningDashboard/leadCategoryBackfill.js) for a date range and polls
// its progress. The API is fire-and-forget (one Node process, no job
// queue, no push updates) - this panel is the only way to watch it move,
// via polling. Checks status once on mount too, so reopening this page
// while a backfill someone else started is still running picks up its
// progress instead of assuming idle.
const LeadCategoryBackfillPanel = () => {
  // Computed once per mount, not re-checked reactively - a login change
  // means a fresh page load anyway.
  const [allowed] = useState(isTechTeam);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [state, setState] = useState(null);
  const [triggerError, setTriggerError] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = () => {
    apiRequest({ url: 'warning-dashboard/lead-category-backfill/status', method: 'get' })
      .then((res) => setState(res?.data?.state || null))
      .catch(() => {});
  };

  // No point even fetching status for someone who can never see or use
  // this panel - hooks still run every render (rules of hooks), the
  // network calls inside them just don't.
  useEffect(() => {
    if (allowed) fetchStatus();
  }, [allowed]);

  useEffect(() => {
    if (allowed && state?.status === 'running') {
      pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
  }, [allowed, state?.status]);

  if (!allowed) return null;

  const handleTrigger = () => {
    setTriggerError(null);
    setTriggering(true);
    apiRequest({ url: 'warning-dashboard/lead-category-backfill', method: 'post', data: { startDate, endDate } })
      .then((res) => setState(res?.data?.state || null))
      .catch((err) => setTriggerError(err?.response?.data?.message || 'Could not start the backfill'))
      .finally(() => setTriggering(false));
  };

  const progressPct = state?.total ? Math.round((state.processed / state.total) * 100) : 0;
  const isRunning = state?.status === 'running';

  return (
    <Box sx={{ mb: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Backfill Lead Categories
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fetches Source / Lead Type II from LeadSquare for leads that existed
        before category tracking was added. Only leads with a known
        LeadSquare id in the selected date range are attempted - the rest
        stay "Uncategorized".
      </Typography>

      <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={isRunning}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={isRunning}
        />
        <Button variant="contained" onClick={handleTrigger} disabled={!startDate || !endDate || triggering || isRunning}>
          {isRunning ? 'Running...' : 'Start Backfill'}
        </Button>
      </Box>

      {triggerError && <Typography color="error.main" sx={{ mb: 2 }}>{triggerError}</Typography>}

      {state && state.status !== 'idle' && (
        <Box>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 1 }}>
            <Chip
              size="small"
              label={state.status}
              color={state.status === 'completed' ? 'success' : state.status === 'failed' ? 'error' : 'default'}
            />
            <Typography variant="body2" color="text.secondary">
              {state.startDate} to {state.endDate}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progressPct} sx={{ mb: 1, height: 8, borderRadius: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {state.processed} / {state.total} processed — {state.synced} synced ({state.withData} with data), {state.failed} failed
          </Typography>
          {state.status === 'failed' && state.error && (
            <Typography color="error.main" variant="body2" sx={{ mt: 1 }}>{state.error}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LeadCategoryBackfillPanel;
